import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  Clock3,
  History,
  Lightbulb,
  ListChecks,
  Target,
  Users,
} from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { InteractiveGameTutorial } from "../components/InteractiveGameTutorial";
import { gameGuideById } from "../gameGuides";
import { gameLaunchPath } from "../gameLaunchIntent";
import type { PlayableRoomGameId } from "../roomGameCatalog";

export function GameTutorialPage() {
  const { gameId } = useParams();
  const guide = gameId ? gameGuideById.get(gameId as PlayableRoomGameId) : undefined;

  if (!guide) return <Navigate to="/how-to-play" replace />;

  return (
    <article className={`guide-page guide-${guide.color}`}>
      <section className="guide-hero">
        <div>
          <Link className="text-link" to="/how-to-play">
            <ArrowLeft /> All game guides
          </Link>
          <span className="eyebrow">HOW TO PLAY</span>
          <h1>{guide.name}</h1>
          <p>{guide.summary}</p>
          <div className="guide-facts">
            <span>
              <Users /> {guide.players}
            </span>
            <span>
              <Clock3 /> {guide.time}
            </span>
          </div>
        </div>
        <div className="guide-hero-art" aria-hidden="true">
          <span>{guide.symbol}</span>
          <small>{guide.tagline}</small>
        </div>
      </section>

      <section className="guide-content">
        <div className="guide-goal">
          <Target />
          <div>
            <span className="eyebrow">THE GOAL</span>
            <h2>{guide.goal}</h2>
          </div>
        </div>

        <section className="guide-history">
          <div className="guide-section-heading">
            <History />
            <div>
              <span className="eyebrow">WHERE IT CAME FROM</span>
              <h2>The story behind {guide.name}</h2>
            </div>
          </div>
          <div>
            {guide.history.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </section>

        <section className="guide-setup">
          <div className="guide-section-heading">
            <BookOpen />
            <div>
              <span className="eyebrow">BEFORE THE FIRST TURN</span>
              <h2>Board and setup</h2>
            </div>
          </div>
          <ol className="friendly-steps">
            {guide.setup.map((step, index) => (
              <li key={step}>
                <span>{index + 1}</span>
                <p>{step}</p>
              </li>
            ))}
          </ol>
        </section>

        <div className="guide-columns">
          <section>
            <span className="section-number">01</span>
            <h2>How a round works</h2>
            <ol className="friendly-steps">
              {guide.steps.map((step, index) => (
                <li key={step}>
                  <span>{index + 1}</span>
                  <p>{step}</p>
                </li>
              ))}
            </ol>
          </section>
          <section>
            <span className="section-number">02</span>
            <h2>Rules to remember</h2>
            <ul className="rule-list">
              {guide.rules.map((rule) => (
                <li key={rule}>
                  <Check /> <span>{rule}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <section className="guide-lessons">
          <div className="guide-section-heading">
            <ListChecks />
            <div>
              <span className="eyebrow">LESSON PATH</span>
              <h2>Skills you will practise</h2>
              <p>
                Work through these in order, then repeat the guided board until each move feels
                natural.
              </p>
            </div>
          </div>
          <ol>
            {guide.lessons.map((lesson, index) => (
              <li key={lesson.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <strong>{lesson.title}</strong>
                  <p>{lesson.outcome}</p>
                </div>
              </li>
            ))}
          </ol>
          {guide.id === "chess" ? (
            <a
              className="guide-reference-link"
              href="https://en.wikipedia.org/wiki/Castling"
              target="_blank"
              rel="noreferrer"
            >
              Read the full castling reference <ArrowRight />
            </a>
          ) : null}
        </section>

        <section className="guide-interactive-section">
          <div className="guide-section-heading">
            <Target />
            <div>
              <span className="eyebrow">TRY IT YOURSELF</span>
              <h2>Playable guided practice</h2>
              <p>
                Follow the coach, make each move yourself, and repeat any lesson you want. Practice
                is local, separate from rooms, and does not affect stats.
              </p>
            </div>
          </div>
          <InteractiveGameTutorial gameId={guide.id} />
        </section>

        <section className="tip-band">
          <Lightbulb />
          <div>
            <span className="eyebrow">PLAY A LITTLE SMARTER</span>
            <h2>Quick tips</h2>
            <div>
              {guide.tips.map((tip) => (
                <p key={tip}>{tip}</p>
              ))}
            </div>
          </div>
        </section>

        <section className="guide-cta">
          <div>
            <span className="eyebrow">READY?</span>
            <h2>Put it into play.</h2>
            <p>Create a room, choose {guide.name}, and invite a friend—or begin with a bot.</p>
          </div>
          <Link className="button button-primary" to={gameLaunchPath(guide.id)}>
            Start a room <ArrowRight />
          </Link>
        </section>
      </section>
    </article>
  );
}
